import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion as Motion } from 'framer-motion';
import { useSessionAccumulator } from '../../contexts/SessionAccumulator';
import useBuddy from '../../hooks/useBuddy';
import { getBuddyAssets, SLOT } from '../../lib/buddyManifest';
import { SPRING_POP } from '../../constants/motion';
import s from './PracticeSummaryPage.module.css';

/**
 * Writing-practice session summary (FP-BUDDY-POLISH-01 §D).
 * All-cream layout: dark "Great Work!" heading, the CELEBRATE buddy centred
 * below it, then the stats grid + "Back to Dashboard". Single cream background,
 * no two-tone split, no divider. Stats come from the session results + XP accumulator.
 */
export default function PracticeSummaryPage({ results = [], onGoHome }) {
  const { t } = useTranslation();
  const { sessionXP } = useSessionAccumulator();
  const buddy = useBuddy();

  // CELEBRATE pose 2 if it exists, else the first (Buddy-Service section J).
  const heroBuddy = useMemo(() => {
    const poses = getBuddyAssets(buddy.slug, SLOT.CELEBRATE);
    return poses[1] || poses[0] || buddy.celebrate;
  }, [buddy.slug, buddy.celebrate]);

  const totalChars = results.length;
  const perfectCount = useMemo(() => results.filter((r) => r.correct).length, [results]);

  return (
    <div className={s.summary}>
      <h1 className={s.title}>{t('practice.greatWork')}</h1>

      {heroBuddy && (
        <Motion.img
          src={heroBuddy}
          alt=""
          aria-hidden="true"
          className={s.buddy}
          onError={(e) => { e.target.style.display = 'none'; }}
          initial={{ opacity: 0, y: 24, scale: 0.85 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={SPRING_POP}
        />
      )}

      <div className={s.body}>
        <div className={s.statsGrid}>
          <div className={s.statRow}>
            <div className={s.statCard}>
              <span className={s.statValue}>{totalChars}</span>
              <span className={s.statLabel}>{t('practice.statCharacters')}</span>
            </div>
            <div className={s.statCard}>
              <span className={s.statValue}>{perfectCount}</span>
              <span className={s.statLabel}>{t('practice.statPerfect')}</span>
            </div>
          </div>
          <div className={s.statRow}>
            <div className={`${s.statCard} ${s.wide}`}>
              <span className={s.statValue}>{sessionXP}</span>
              <span className={s.statLabel}>{t('practice.statXpEarned')}</span>
            </div>
          </div>
        </div>

        <div className={s.actions}>
          <button type="button" className={s.homeButton} onClick={onGoHome}>
            {t('practice.backToDashboard')}
          </button>
        </div>
      </div>
    </div>
  );
}
